import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { map, of, switchMap } from 'rxjs'
import { AcceptedLead, ConvertedLead, ConvertLead, Lead, LeadStatus, WithSource } from '@features/lead/lead.model'
import { AdditionalInfoSection } from '@features/client/client.model'
import { MarketingMetricsStoreService } from '@features/marketing-metrics-store/marketing-metrics-store.service'
import { createProgressStepHttpContext } from '@features/progress/util'
import { ACTION_STEP_NAME } from '@features/progress/contracts'
import { AuthService } from "@features/auth/auth.service"
import { nullOnHttpNotFound } from "@sun-finance/utils"

@Injectable({
  providedIn: 'root'
})
export class LeadService {

  constructor(private http: HttpClient, private metricsStore: MarketingMetricsStoreService, private auth: AuthService) { }

  private get basePath(): string {
    return `/${this.auth.authenticated ? 'auth' : 'anon'}/kt-lead/leads`
  }

  private get lookupSaasFallbackPath(): string {
    return `/${this.auth.authenticated ? 'auth' : 'anon'}/lead-lookup`
  }

  byId(id: string) {
    return this.http.get<Lead>(`${this.basePath}/${id}`).pipe(
      nullOnHttpNotFound(),
      switchMap(lead => {
        if (lead !== null) {
          return of<WithSource<Lead>>({ ...lead, source: 'product' })
        }
        return this.http.get<Lead>(`/anon/lead-find/${id}`).pipe(
          nullOnHttpNotFound(),
          map(lead => lead ? { ...lead, source: 'saas' } as WithSource<Lead> : null)
        )
      })
    )
  }

  lookup() {
    return this.http.get<AcceptedLead>(`${this.lookupSaasFallbackPath}`).pipe(
      nullOnHttpNotFound(),
      switchMap(lead => {
        if (lead !== null) {
          return of<WithSource<AcceptedLead>>({ ...lead, source: 'saas' })
        }
        return this.http.get<AcceptedLead>(`${this.basePath}/lookup`).pipe(
          nullOnHttpNotFound(),
          map(lead => lead ? { ...lead, source: 'product' } as WithSource<AcceptedLead> : null)
        )
      })
    )
  }

  accepted(id: string) {
    return this.byId(id).pipe(
      map(lead => (lead && [LeadStatus.CONVERTED, LeadStatus.ACCEPTED].includes(lead.status)) ? lead as WithSource<AcceptedLead> : null)
    )
  }

  convert(id: string, command: Partial<ConvertLead>, source: 'product' | 'saas') {
    const context = createProgressStepHttpContext({
      stepName: ACTION_STEP_NAME.LEAD_CONVERTED,
      withMetadata: ({ clientId, conversionActions, status }: ConvertedLead) => ({ clientId, status, ...conversionActions })
    })

    const metrics = this.metricsStore.retrieve()
    const additionalInfo = { ...command.additionalInfo, ...(metrics ? { [AdditionalInfoSection.MARKETING]: metrics } : {}) }

    const url = source === 'product'
      ? `${this.basePath}/${id}/conversion`
      : `/anon/lead-convert/${id}`

    return this.http.put<ConvertedLead>(url, { ...command, additionalInfo }, { context })
  }
}
